import { useEffect, useMemo, useRef, useState } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  MaterialReactTable,
  MRT_RowSelectionState,
  useMaterialReactTable,
  type MRT_ColumnFiltersState,
  type MRT_PaginationState,
  type MRT_SortingState,
} from "material-react-table";
import { Updater } from "@tanstack/react-table";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import SwitchComponent from "../Client/SwitchComponent";
import "../../styles/common.css";

interface DataTableProps {
  header?: string;
  columns: any[];
  rows: any[];
  rowCount: number;
  initialSort?: any;
  initialFilter?: any;
  columnVisibility?: any;
  renderRowActionMenuItems?: (row: any, closeMenu: any) => any;
  parentContainerHeight?: number;
  startEndTime?: { start_time: string; end_time: string };
  columnsForGraphicalDetails?: string[];
  onCellClick?: (cellValue: any) => void;
  hidePaginationFooter?: boolean;
  onPageSizeChange?: (newPageSize: number) => void;
  onSortingChange?: (sorting: any[]) => void;
  onPaginationChange?: (pageIndex: number) => void;
  rated?: boolean;
  totalCount?: number;
  ratedCount?: number;
  onToggleChange?: (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => void;
  showSwitch?: boolean;  // Determines whether the switch is shown
  tabLabel?: string;
  minHeight?: string;
  isBordervisible?: boolean;
  customToolbarComponent?: React.ReactNode;
  maxHeight?: string;
  showSpinner?: boolean;
  rowHoverEnabled?: boolean;
  height?: string;
  enableRowSelection?: boolean;
  selectedRowIds?: any[];
  onRowSelectionChange?: (selectedRows: any[]) => void;
  checkboxStyle?: { size?: "small" | "medium"; color?: string };
  disabledRowIds?: string[];
  hiddenSelectRowIds?: string[];
  muiTableBodyRowProps?: any;
  enableExpanding?: boolean;
  enableSubRowSelection?: boolean;
  expanded?: Record<string, boolean>;
  onExpandedChange?: (expanded: Record<string, boolean>) => void;
}

const BasicDatatable: React.FC<DataTableProps> = ({
  header,
  columns,
  rows,
  rowCount,
  initialSort,
  columnVisibility,
  // rowActions,
  renderRowActionMenuItems,
  // parentContainerHeight,
  startEndTime,
  columnsForGraphicalDetails,
  onCellClick,
  hidePaginationFooter = false,
  onPageSizeChange,
  onSortingChange,
  onPaginationChange,
  rated,
  totalCount,
  ratedCount,
  onToggleChange,
  showSwitch,
  tabLabel,
  minHeight,
  isBordervisible,
  customToolbarComponent,
  maxHeight,
  showSpinner,
  rowHoverEnabled = false,
  height,
  enableRowSelection = false,
  selectedRowIds,
  onRowSelectionChange,
  checkboxStyle,
  disabledRowIds,
  hiddenSelectRowIds,
  muiTableBodyRowProps,
  enableExpanding = false,
  enableSubRowSelection = true,
  expanded,
  onExpandedChange,
}) => {
  const theme = useTheme();
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 50,
  });
  const [sorting, setSorting] = useState<MRT_SortingState>(
    initialSort
      ? [
          {
            id: initialSort.sort_field,
            desc: initialSort.sort_order === "desc" ? true : false,
          },
        ]
      : []
  );
  useEffect(() => {
    setSorting(
      initialSort
        ? [
            {
              id: initialSort.sort_field,
              desc: initialSort.sort_order === "desc" ? true : false,
            },
          ]
        : []
    );
  }, [initialSort]);  

  useEffect(() => {
    if (onSortingChange) {
      onSortingChange(sorting);
    }
  }, [sorting]);

  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>(
    []
  );
  useEffect(() => {
    if (onPaginationChange) {
      onPaginationChange(pagination.pageIndex);
    }
  }, [pagination.pageIndex]);
  
  useEffect(() => {
    if (tableContainerRef.current) {
      tableContainerRef.current.scrollTop = 0;
    }
  }, [pagination.pageIndex]); 

  const tableContainerRef = useRef<HTMLDivElement | null>(null);
  const controlledRowSelection = useMemo(() => {
    const selectionMap: Record<string, boolean> = {};
    if (enableRowSelection && selectedRowIds && selectedRowIds.length > 0) {
      selectedRowIds.forEach((id) => {
        selectionMap[id] = true;
      });
    }
    return selectionMap;
  }, [selectedRowIds, enableRowSelection]);

  const allRowsDisabled = useMemo(() => {
    if (!rows?.length) return false;
    const blockedIds = [...(disabledRowIds || []), ...(hiddenSelectRowIds || [])];
    if (!blockedIds.length) return false;
    return rows.every((row) => blockedIds.includes(row.id));
  }, [rows, disabledRowIds, hiddenSelectRowIds]);

  const handleRowSelectionChange = (updater: Updater<MRT_RowSelectionState>) => {
    let newSelection: MRT_RowSelectionState;
    if (typeof updater === "function") {
      newSelection = updater(controlledRowSelection);
    } else {
      newSelection = updater;
    }

    const selectedIds = Object.keys(newSelection).filter((id) => newSelection[id]);
    onRowSelectionChange?.(selectedIds);
  };
  
  const table = useMaterialReactTable({
    layoutMode: "grid",
    columns,
    data: rows,
    getRowId: (row) => row.id,
    initialState: {
      showColumnFilters: true,
      pagination: pagination as MRT_PaginationState,
      columnFilters,
    },
    enableTopToolbar: header ? true : false,
    enableColumnActions: false,
    enableColumnFilters: false,
    enableColumnFilterModes: false,
    enableBottomToolbar: !hidePaginationFooter,
    enableStickyHeader: true,
    manualPagination: true, // Enable external pagination management
    manualSorting: true,
    manualFiltering: true,
    enableSortingRemoval: false,
    onPaginationChange: setPagination,    
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    rowCount,
    enableRowSelection: enableRowSelection
      ? (row: any) =>
          !disabledRowIds?.includes(row?.id) &&
          !hiddenSelectRowIds?.includes(row?.id)
      : false,
    enableExpanding,
    enableExpandAll: false,
    enableSubRowSelection: enableExpanding ? enableSubRowSelection : undefined,
    getSubRows: enableExpanding ? (row) => row.subRows : undefined,
    onExpandedChange: enableExpanding && onExpandedChange
      ? (updater) => {
          const current = expanded || {};
          const next =
            typeof updater === "function" ? updater(current) : updater;
          onExpandedChange((next as Record<string, boolean>) || {});
        }
      : undefined,
    onRowSelectionChange: enableRowSelection ? handleRowSelectionChange : undefined,
    muiSelectCheckboxProps: ({ row }) => {
      const isDisabled = Boolean(disabledRowIds?.includes(row?.id));
      const isHidden = Boolean(hiddenSelectRowIds?.includes(row?.id));

      return {
        size: checkboxStyle?.size || "small",
        disabled: isDisabled || isHidden,
        title: isDisabled || isHidden ? "" : "Select",
        sx: {
          visibility: isHidden ? "hidden" : "visible",
          pointerEvents: isHidden ? "none" : undefined,
          borderColor: "#C2C2CA",
          color: isDisabled ? "#ccc" : checkboxStyle?.color || "#666",
          cursor: isDisabled || isHidden ? "not-allowed" : "pointer",
          "&.Mui-disabled": {
            color: "#ccc",
          },
          "&.Mui-checked": {
            color: checkboxStyle?.color || "#00C2A8",
          },
          "&.MuiCheckbox-indeterminate": {
            color: checkboxStyle?.color || "#00C2A8",
          },
          "& .MuiSvgIcon-root": {
            width:
              checkboxStyle?.size === "small"
                ? "13.375px"
                : checkboxStyle?.size === "medium"
                ? "16px"
                : "16px",
            height:
              checkboxStyle?.size === "small"
                ? "13.375px"
                : checkboxStyle?.size === "medium"
                ? "16px"
                : "16px",
          },
        },
      };
    },
    muiSelectAllCheckboxProps: {
      size: checkboxStyle?.size || "small",
      disabled: allRowsDisabled,
      title: allRowsDisabled ? "" : "Select all",
      sx: {
        verticalAlign: "middle",
        transform: "translatex(-1.5px)",
        color: allRowsDisabled ? "#ccc" : checkboxStyle?.color || "#666",
        "&.Mui-checked": {
          color: checkboxStyle?.color || "#00C2A8",
        },
        "&.MuiCheckbox-indeterminate": {
          color: checkboxStyle?.color || "#00C2A8",
        },
        "& .MuiSvgIcon-root": {
          width:
            checkboxStyle?.size === "small"
              ? "13.375px"
              : checkboxStyle?.size === "medium"
              ? "16px"
              : "16px",
          height:
            checkboxStyle?.size === "small"
              ? "13.375px"
              : checkboxStyle?.size === "medium"
              ? "16px"
              : "16px",
        },
      },
      onChange: !allRowsDisabled
        ? () => {
            const isIndeterminate = table.getIsSomeRowsSelected();
            const isAllSelected = table.getIsAllRowsSelected();

            if (isIndeterminate || isAllSelected) {
              table.toggleAllRowsSelected(false);
            } else {
              const selectableRowIds = table
                .getRowModel()
                .rows.filter(
                  (row) =>
                    !disabledRowIds?.includes(row.id) &&
                    !hiddenSelectRowIds?.includes(row.id)
                )
                .map((row) => row.id);

              const newSelection: Record<string, boolean> = {};
              selectableRowIds.forEach((id) => {
                newSelection[id] = true;
              });

              table.setRowSelection(newSelection);
            }
          }
        : undefined,
    },
    displayColumnDefOptions: {
      "mrt-row-select": {
        size: 34,
        minSize: 34,
        maxSize: 34,
      },
      ...(enableExpanding
        ? {
            "mrt-row-expand": {
              size: 0,
              minSize: 0,
              maxSize: 0,
              header: "",
            },
          }
        : {}),
    },
    renderEmptyRowsFallback: () =>
      showSpinner ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            py: 5,
            width: "100%",
          }}
        >
          <CircularProgress sx={{ color: "#ccc", opacity: 0.5 }} thickness={5} size={60} />
        </Box>
      ) : (
        <Box sx={{ p: 2, textAlign: "center" }}>No data</Box>
      ),
    state: {
      columnFilters,
      pagination,
      sorting,
      columnVisibility: {
        ...columnVisibility,
        ...(enableExpanding ? { "mrt-row-expand": false } : {}),
      },
      rowSelection: enableRowSelection ? controlledRowSelection : {},
      ...(enableExpanding && expanded ? { expanded } : {}),
      columnPinning: enableRowSelection
        ? { left: ["mrt-row-select"], right: ["mrt-row-actions"] }
        : { right: ["mrt-row-actions"] },
    },
    enableRowActions: renderRowActionMenuItems !== undefined ? true : false,
    positionActionsColumn: "last",
    renderRowActionMenuItems: ({ row, closeMenu }) => {
      if (renderRowActionMenuItems) {
        const rowActionComponent: any = renderRowActionMenuItems(
          row,
          closeMenu
        );
        return rowActionComponent;
      }
      return null;
    },
    renderTopToolbarCustomActions: () => {
      return header ? (
        <Typography variant="h5" sx={{ fontWeight: 500 }}>
          {header}
        </Typography>
      ) : (
        <></>
      );
    },
    muiTablePaperProps: {
      sx: {
        width: "100%",
        fontFamily: theme.typography.fontFamily,
        fontWeight: theme.typography.body1.fontWeight,
        color: theme.palette.text.primary,
        fontSize: theme.typography.body1.fontSize,
        [theme.breakpoints.between("md", "lg")]: {
          fontSize: "14px",
        },
        [theme.breakpoints.between("sm", "md")]: {
          fontSize: "12px",
        },
        [theme.breakpoints.down("sm")]: {
          fontSize: "12px",
        },
        boxShadow: "none",
      },
    },
    muiTableHeadRowProps: {
      sx: {
        backgroundColor: "#FFFFFF",
        boxShadow: "none",
        height: "45px",
        marginTop: "-12px",
        position: "sticky", // Fix the header position
        top: 0, // Stick it to the top
        zIndex: 2, // Keep it above other elements
      },
    },
    muiTableHeadCellProps: {
      className: "mui-table-head-cell",
      sx: {
        padding: "20px 5px",
        fontFamily: 'var(--font-family)',
        fontWeight: 600,  // Set font weight to 600
        fontSize: "15px",  // Set font size to 15px
        lineHeight: "18px",  // Set line height to 18px
        letterSpacing: "0px",  // Set letter-spacing to 0px
        color: "#33333B",
        borderBottom: "1px solid #7A7A8C",
        [theme.breakpoints.between("md", "lg")]: {
          fontSize: "13px",
        },
        [theme.breakpoints.between("sm", "md")]: {
          fontSize: "13px",
        },
        [theme.breakpoints.down("sm")]: {
          fontSize: "13px",
        },
        boxShadow: "none",
        "& .MuiTableSortLabel-root": {
          color: "#33333B",
          display: "inline-flex",
          alignItems: "center",
        },

        "& .MuiTableSortLabel-root:hover": {
          color: "#6036D6",
        },

        "& .MuiTableSortLabel-root.Mui-active": {
          color: "#6036D6",
        },

        "& .MuiTableSortLabel-icon": {
          color: "#9595A3 !important",
          opacity: 1,
          margin: "0 4px !important",
        },

        "& .MuiTableSortLabel-root.Mui-active .MuiTableSortLabel-icon": {
          color: "#6036D6 !important",
        },

        "& .MuiTableSortLabel-root:hover .MuiTableSortLabel-icon": {
          color: "#6036D6 !important",
        },
      },
    },
    muiTableBodyRowProps: ({ row }) => {
      const defaultRowProps = {
        hover: rowHoverEnabled,
        sx: {
          backgroundColor: "transparent",
          height: "40px",
          minHeight: "40px",
          transition: "background-color 200ms ease, color 200ms ease",
          ...(rowHoverEnabled
            ? {
                "&:hover": { backgroundColor: "transparent" },
                "&:hover td, &:hover th": {
                  backgroundColor: "#F2F2F4",
                  cursor: "pointer",
                },
              }
            : {
                "&:hover td, &:hover th": {
                  backgroundColor: "transparent !important",
                  cursor: "default !important",
                },
              }),
        },
      };

      const customRowProps =
        typeof muiTableBodyRowProps === "function"
          ? muiTableBodyRowProps({ row })
          : muiTableBodyRowProps || {};

      return {
        ...defaultRowProps,
        ...customRowProps,
        className: [
          (defaultRowProps as any)?.className,
          (customRowProps as any)?.className,
        ]
          .filter(Boolean)
          .join(" "),
        sx: {
          ...(defaultRowProps as any).sx,
          ...(customRowProps as any).sx,
        },
      };
    },
    muiTableBodyCellProps: ({ cell, row }) => ({
      className: "mui-table-body-cell",
      sx: {
        padding: "10px 5px",
        fontFamily: 'var(--font-family)',
        fontWeight: 400,  // Set font weight to 400
        fontSize: "14px",  // Set font size to 14px
        lineHeight: "16px",  // Set line height to 18px
        letterSpacing: "0px",  // Set letter-spacing to 0px
        color: "#43434D",
        [theme.breakpoints.between("md", "lg")]: {
          fontSize: "13px",
        },
        [theme.breakpoints.between("sm", "md")]: {
          fontSize: "13px",
        },
        [theme.breakpoints.down("sm")]: {
          fontSize: "13px",
        },
      },
      onClick: (_event) => {
        if (columnsForGraphicalDetails && columnsForGraphicalDetails.length > 0)
          columnsForGraphicalDetails.includes(cell.column.id) &&
            onCellClick &&
            onCellClick(row.original);
      },
    }),
    muiTableContainerProps: {
      ref: tableContainerRef,
      sx: {
        padding: "12px",
        height: height ||  "calc(50vh + 50px)",
        minHeight: minHeight || "280px",
        maxHeight: maxHeight || "500px", // Or any specific value
        overflowY: "auto", // Allows scrolling within a fixed height
        scrollbarWidth: "thin",           // Firefox
        "&::-webkit-scrollbar": {         // Chrome, Edge, Safari
          width: "4px",
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: "#ccc",
          borderRadius: "4px",
        },
        zIndex:1
      },
    },
    muiPaginationProps: {
      showRowsPerPage: true,
    },
    muiBottomToolbarProps: {
      className: "mui-bottom-toolbar",
    },
    muiSearchTextFieldProps: {
      size: "small",
      variant: "outlined",
    },
    muiFilterTextFieldProps: {
      variant: "outlined",
      size: "small",
    },

    muiFilterDatePickerProps: startEndTime
      ? {
          minDate: dayjs(startEndTime.start_time),
          maxDate: dayjs(startEndTime.end_time),
        }
      : undefined,
  });
  const handlePageSizeChange = (newPageSize: number) => {
    onPageSizeChange && onPageSizeChange(newPageSize);
  };
  useEffect(()=>{
    handlePageSizeChange(pagination.pageSize)
  },[pagination.pageSize])

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box
        sx={{
        ...(isBordervisible ? {} : { border: "1px solid rgba(229, 229, 229, 1)" }),
          borderRadius: "8px",
          marginTop :"0px",
        }}
      >
        {showSwitch && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: rated? 'space-between' : 'flex-end',
            alignItems: 'center',
            padding: '0px 15px',
            gap: '10px',
            borderBottom: '1px solid rgba(229, 229, 229, 1)', // <- Add this line
          }}
        >
          {customToolbarComponent}
          <SwitchComponent
            rated={rated}
            totalCount={totalCount}
            ratedCount={ratedCount}
            onToggleChange={onToggleChange}
            hideUnderline={true}
          />
        </Box>
      )}
        <Box sx={{padding:'0px 10px'}}>
          <Box sx={{ display: 'flex', alignItems: 'center', padding: tabLabel ? "15px 0px 10px 15px" : "0px", gap: "10px"}}>
            <h1 className="datatable__title" style={{ margin: 0 }}>{tabLabel}</h1>
            {!showSwitch && (  customToolbarComponent)}
          </Box>
            
          
        <MaterialReactTable table={table} />
        
        </Box>
      </Box>
    </LocalizationProvider>
  );
};

export default BasicDatatable;
